import { inject, injectable } from 'inversify';
import type { types } from 'mediasoup-client';
import type { Transport } from 'mediasoup-client/lib/Transport';
import { serializeError } from 'serialize-error';
import { FIVE_SECONDS_IN_MS } from '../../constants/common';
import { TOKEN } from '../../inversify.tokens';
import type { Logger } from '../../types/common';
import type { LoggerFactory } from '../../types/container';
import { LDTransport } from '../../types/mediasoup';

@injectable()
class DatachannelHeartbeater {
  #heartbeatIntervalId?: ReturnType<typeof setInterval>;

  #dataProducer?: types.DataProducer;

  #transport?: Transport;

  #dataProducerCleanups: Array<() => void> = [];

  readonly #logger: Logger;

  readonly #heartbeatPayload = new Uint8Array(0);

  constructor(
    @inject(TOKEN.LoggerFactory) loggerFactory: LoggerFactory,
  ) {
    this.#logger = loggerFactory('DatachannelHeartbeater');
  }

  init(transport: LDTransport, dataProducer: types.DataProducer): void {
    this.#transport = transport;
    this.#dataProducer = dataProducer;
    this.subscribeToDataProducerEvents(dataProducer);
    this.startHeartbeatInterval();
  }

  dispose(): void {
    this.stopHeartbeatInterval();
    this.cleanupDataProducerEvents();
    this.#dataProducer = undefined;
    this.#transport = undefined;
  }

  private startHeartbeatInterval(): void {
    if (this.#heartbeatIntervalId) {
      return;
    }

    this.#heartbeatIntervalId = setInterval(() => {
      this.sendHeartbeat();
    }, FIVE_SECONDS_IN_MS);
    this.#logger.debug('Datachannel heartbeat interval started');
  }

  private stopHeartbeatInterval(): void {
    if (!this.#heartbeatIntervalId) {
      return;
    }

    clearInterval(this.#heartbeatIntervalId);
    this.#heartbeatIntervalId = undefined;
    this.#logger.debug('Datachannel heartbeat interval stopped');
  }

  private canSendHeartbeat(): boolean {
    if (!this.#dataProducer || !this.#transport) {
      return false;
    }

    if (this.#dataProducer.closed || this.#transport.connectionState !== 'connected') {
      return false;
    }

    return this.#dataProducer.readyState === 'open';
  }

  private subscribeToDataProducerEvents(dataProducer: types.DataProducer): void {
    this.cleanupDataProducerEvents();

    const logState = (event: string): void => {
      this.#logger.debug(`Heartbeat data producer ${ event }`, {
        readyState: dataProducer.readyState,
        closed: dataProducer.closed,
      });
    };

    const onOpen = (): void => logState('open');
    const onClose = (): void => logState('close');
    const onTransportClose = (): void => logState('transport closed');

    dataProducer.on('open', onOpen);
    dataProducer.on('close', onClose);
    dataProducer.on('transportclose', onTransportClose);

    this.#dataProducerCleanups = [
      () => dataProducer.off('open', onOpen),
      () => dataProducer.off('close', onClose),
      () => dataProducer.off('transportclose', onTransportClose),
    ];
  }

  private cleanupDataProducerEvents(): void {
    this.#dataProducerCleanups.forEach((cleanup) => cleanup());
    this.#dataProducerCleanups = [];
  }

  private sendHeartbeat(): void {
    if (!this.#dataProducer || !this.canSendHeartbeat()) {
      return;
    }

    try {
      this.#dataProducer.send(this.#heartbeatPayload);
    } catch (error) {
      this.#logger.debug('Failed to send datachannel heartbeat', {
        error: serializeError(error),
      });
    }
  }
}

export default DatachannelHeartbeater;
